import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useLocation } from "wouter";
import { AuthButton } from "@/components/auth-button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Mail, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { MfaVerifyLogin } from "@/components/mfa/verify-login";

const loginSchema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean().optional(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const [mfaPendingVerification, setMfaPendingVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState<string | null>(null);
  const { user, loginMutation } = useAuth();

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  const onLoginSubmit = (data: LoginFormValues) => {
    if (!data.email || !data.email.includes('@')) {
      loginForm.setError('email', {
        type: 'manual',
        message: 'Please enter a valid email address'
      });
      return;
    }
    if (!data.password || data.password.length < 4) {
      loginForm.setError('password', {
        type: 'manual',
        message: 'Password is too short'
      });
      return;
    }
    loginForm.clearErrors();
    setLockoutMessage(null); // Clear any previous lockout message
    loginMutation.mutate({
      username: data.email, // Backend still expects username field for now
      password: data.password,
    }, {
      onSuccess: (userData) => {
        if ((userData as any).requiresMfa) {
          setMfaPendingVerification(true);
        } else {
          navigate("/");
        }
      },
      onError: (error: Error) => {
        // Check if this is a lockout message
        if (error.message.includes("locked") || error.message.includes("attempts")) {
          setLockoutMessage(error.message);
        } else {
          setLockoutMessage(null);
        }
      }
    });
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50 dark:bg-gray-900">
      {mfaPendingVerification && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <MfaVerifyLogin 
            onVerified={() => {
              setMfaPendingVerification(false);
              navigate("/");
            }} 
            onCancel={() => {
              setMfaPendingVerification(false);
              loginForm.reset();
            }} 
          />
        </div>
      )}

      {/* Left column – Login form */}
      <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden dark:border dark:border-gray-700 flex flex-col items-center">
          <div className="p-8 w-full">
            <h2 className="text-2xl font-semibold tracking-wide text-center text-gray-800 dark:text-white mb-8">
              Sign in to your account
            </h2>
            {lockoutMessage && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      Account Temporarily Locked
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                      {lockoutMessage}
                    </p>
                  </div>
                </div>
                {lockoutMessage.includes("15 minutes") && (
                  <div className="mt-3 flex items-center text-sm text-red-600 dark:text-red-300">
                    <Clock className="h-4 w-4 mr-2" />
                    <span>Please wait 15 minutes before trying again</span>
                  </div>
                )}
              </div>
            )}
            <Form {...loginForm}>
              <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-6">
                <div className="space-y-1">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input 
                            placeholder="your@email.com" 
                            className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <a href="/forgot-password" className="text-xs text-[#4A6CA7] hover:text-[#0A51A8] font-medium">
                      Forgot password?
                    </a>
                  </div>
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              type={showPassword ? "text" : "password"} 
                              placeholder="Enter your password" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10" 
                              {...field} 
                            />
                            <button 
                              type="button"
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex={-1}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                              ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                              )}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500 text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loginMutation.isPending}
                  variant="default"
                  className="w-full"
                >
                  {loginMutation.isPending ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      <span>Signing in...</span>
                    </span>
                  ) : "Login"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
        <div className="mt-6 text-center">
          <span className="text-gray-600 dark:text-gray-300 text-sm">Don't have an account yet? </span>
          <a href="/signup" className="text-[#4A6CA7] hover:text-[#0A51A8] text-sm font-medium underline">Sign up now</a>
        </div>
        <footer className="py-4 text-center text-gray-500 text-sm lg:w-full">
          © blockseer.com. All rights reserved.
        </footer>
      </main>

      {/* Right column – Splash image */}
      <div className="w-full lg:w-1/2 relative hidden lg:block">
        <img src="/assets/login_spash.png" alt="Signup visual" className="w-full h-full object-cover" />
        <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
        <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
          <p className="font-bold">Connecting the blocks.</p>
          <p className="font-normal">Search. Discover. Analyze.</p>
        </div>
        <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
      </div>
    </div>
  );
} 
